"use client";
import React, { useEffect, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { useAppSelector } from "../../../app/GlobalRedux/store";
import { useGoogleReCaptcha } from "react-google-recaptcha-hook";
import HibtService from "@/services/hibtService";
import { ListTypeId, SearchResultItem, SearchType } from "@/types";
import { itemsPerPageOptions } from "@/utils/consts";
import SearchPageLayout from "@/components/Layouts/SearchPageLayout/SearchPageLayout";
import Table from "@/components/Table/Table";
import LoadingState from "@/components/states/LoadingState";
import InfoSection from "@/components/InfoSection/InfoSection";
import TableControls from "@/components/Table/TableControls/TableControls";
import { useDispatch } from "react-redux";
import { setSnackBar } from "../../../app/GlobalRedux/Features/snackBarSlice";
import PaginationWrapper from "@/components/PaginationWrapper/PaginationWrapper";
import EmptyState from "@/components/states/EmptyState/EmptyState";
import ErrorState from "@/components/states/ErrorState/ErrorState";
import Logging from "@/utils/Logging";

const ImageUploadSearchResultPage = () => {
  const { executeGoogleReCaptcha } = useGoogleReCaptcha(
    process.env.NEXT_PUBLIC_REACT_GACAPTV3_SITE_KEY || "",
    { hide: true }
  );

  const dispatch = useDispatch();
  const currentSearchValue = useAppSelector((state) => state.search.value);
  const thumb = useAppSelector((state) => state.search.thumb);
  const user = useAppSelector((state) => state.auth.user);
  const lists = useAppSelector((state) => state.auth.lists);

  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  const { doNotTrainList } = useAppSelector((state) => state.doNotTrainData);

  const [doNotTrainListData, setDoNotTrainListData] = useState(doNotTrainList);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [dataTotal, setDataTotal] = useState(0);
  const [data, setData] = useState<SearchResultItem[]>([]);
  const [dataToDisplay, setDataToDisplay] = useState<SearchResultItem[]>([]);

  // pagination items
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    itemsPerPageOptions[1].value
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    count: 0,
    from: 0,
    to: itemsPerPage,
  });

  const loadSearchResultsData = async () => {
    setIsLoading(true);

    try {
      const captchaKey = await executeGoogleReCaptcha("submit");

      const { records } = await HibtService.search({
        image: currentSearchValue || undefined,
        CaptchaResponse: captchaKey,
        skip: 0,
        limit: 10
      });

      const transformedData = await records.map((searchResult) => ({
        ...searchResult,
        isChecked: false,
      }));

      // Filter out items that are in the "Do Not Train" list
      const filteredData = transformedData.filter(
        (item) => !doNotTrainListData.map(({ url }) => url).includes(item.url)
      );

      setData(filteredData);
      setDataTotal(filteredData.length);
      setPagination({ ...pagination, count: filteredData.length });
      setIsLoading(false);

      startTransition(() => {
        router.refresh();
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setIsLoading(false);
      setIsError(true);

      Logging.networkError("API Error - HIBT service search", {
        pageName: "ImageUploadSearchResultPage",
        errorMessage: error.message,
        errorCode: error.code,
        statusCode: error.response?.status,
      });

      dispatch(
        setSnackBar({
          open: true,
          message: `An error occurred: ${error.message}`,
          variant: "error",
        })
      );
    }
  };

  useEffect(() => {
    loadSearchResultsData();
  }, [currentSearchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const paginatedData = data.slice(pagination.from, pagination.to);

    setDataToDisplay(paginatedData);
  }, [data, pagination.from, pagination.to]);

  useEffect(() => {
    // calculate the new page number based on the new items per page
    let newPageNumber = Math.ceil((pagination.from + 1) / itemsPerPage);
    if (newPageNumber <= 0) newPageNumber = 1;
    setCurrentPage(newPageNumber);

    // calculate new from and to
    const from = (newPageNumber - 1) * itemsPerPage;
    const to = itemsPerPage * newPageNumber;

    setPagination({ ...pagination, from, to });
  }, [itemsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOptingOutItems = async () => {
    if (!user || !lists) return;
    setIsLoading(true);

    const selectedItems = dataToDisplay.filter(({ isChecked }) => isChecked);

    const doNotTrainList = lists.find(
      ({ type_id }) => type_id === ListTypeId.DO_NOT_TRAIN
    );

    const dataToSend = selectedItems.map(({ id, url, caption }) => ({
      id,
      url,
      caption,
      type_id: ListTypeId.DO_NOT_TRAIN,
      list_id: doNotTrainList?.id || 0,
    }));

    try {
      const optOutResponse =
        await HibtService.insertMultipleObjectsToSelections(
          user.id_token,
          dataToSend
        );

      if (optOutResponse.status === 200) {
        // Get IDs of items opted out
        const optedOutIds = selectedItems.map((item) => item.id);

        // Update local "Do Not Train" list state
        setDoNotTrainListData((prevList) => [...prevList, ...selectedItems]);

        // Filter out opted-out items from the current display and the main data
        const updatedDisplayData = dataToDisplay.filter(
          (item) => !optedOutIds.includes(item.id)
        );
        const updatedSearchData = data.filter(
          (item) => !optedOutIds.includes(item.id)
        );

        setDataToDisplay(updatedDisplayData);
        setData(updatedSearchData);
        setDataTotal(updatedSearchData.length);
        setPagination({ ...pagination, count: updatedSearchData.length });

        await dispatch(
          setSnackBar({
            open: true,
            message: `${dataToSend.length} ${
              dataToSend.length === 1 ? "item" : "items"
            } add to your`,
            variant: "doNotTrain",
          })
        );
      } else {
        dispatch(
          setSnackBar({
            open: true,
            message: `Failed to add ${dataToSend.length} ${
              dataToSend.length === 1 ? "item" : "items"
            } to your`,
            variant: "doNotTrainError",
          })
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      Logging.networkError("API Error - handle opting out items", {
        pageName: "ImageUploadSearchResultPage",
        errorMessage: error.message,
        errorCode: error.code,
        statusCode: error.response?.status,
      });
    }

    setIsLoading(false);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    event.preventDefault();
    const from = (page - 1) * itemsPerPage;
    const to = itemsPerPage * page;

    setCurrentPage(page);
    setPagination({ ...pagination, from, to });
    if (window && typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  };

  const handleCheckBoxClick = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    const { checked } = e.target;

    const newSelectionArray = dataToDisplay.map((searchresult) =>
      searchresult.id === id
        ? { ...searchresult, isChecked: checked }
        : searchresult
    );

    setDataToDisplay(newSelectionArray);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    id: number
  ) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).checked = true;

      const { checked } = e.target as HTMLInputElement;

      const newSelectionArray = dataToDisplay.map((searchresult) =>
        searchresult.id === id
          ? { ...searchresult, isChecked: checked }
          : searchresult
      );

      setDataToDisplay(newSelectionArray);
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      (e.target as HTMLInputElement).checked = false;

      const { checked } = e.target as HTMLInputElement;

      const newSelectionArray = dataToDisplay.map((searchResult) =>
        searchResult.id === id
          ? { ...searchResult, isChecked: checked }
          : searchResult
      );

      setDataToDisplay(newSelectionArray);
    }
  };

  const handleOnSelectAllClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    const selectAllItems = dataToDisplay.map((searchResult) => ({
      ...searchResult,
      isChecked: checked,
    }));

    setDataToDisplay(selectAllItems);
  };

  const handleKeyDownSelectAll = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).checked = true;

      const { checked } = e.target as HTMLInputElement;

      const selectAllItems = dataToDisplay.map((searchResult) => ({
        ...searchResult,
        isChecked: checked,
      }));

      setDataToDisplay(selectAllItems);
    }
  };

  if (isLoading || isPending) {
    return <LoadingState />;
  }

  return (
    <SearchPageLayout>
      {isError && <ErrorState />}

      <InfoSection
        valueSearched={{
          value: thumb as string,
          type: SearchType.IMAGE,
        }}
        label="Image upload search"
        tooltipText={`The top result has ${(data[0]?.similarity * 100).toFixed(
          0
        )}%
        similarity with the searched image`}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        paginationCount={pagination.count}
        handlePageChange={handlePageChange}
      />
      <TableControls
        totalItems={dataTotal}
        pageNumber={currentPage}
        displayedItems={dataToDisplay.length}
        checkedItems={dataToDisplay.filter((domain) => domain.isChecked).length}
        itemsPerPage={itemsPerPage}
        itemsPerPageOptions={itemsPerPageOptions}
        setItemsPerPage={setItemsPerPage}
        handleOnSelectAllClick={handleOnSelectAllClick}
        handleKeyDownSelectAll={handleKeyDownSelectAll}
        handleOptingOutItems={handleOptingOutItems}
      />

      {dataToDisplay.length > 0 && !isLoading && !isError ? (
        <Table
          dataToDisplay={dataToDisplay}
          handleCheckBoxClick={handleCheckBoxClick}
          handleKeyDown={handleKeyDown}
        />
      ) : (
        <EmptyState>
          We were unable to find any results for your search. Please try again
        </EmptyState>
      )}

      {!isLoading && dataToDisplay.length > 0 && (
        <PaginationWrapper
          currentPage={currentPage}
          paginationCount={pagination.count}
          itemsPerPage={itemsPerPage}
          handlePageChange={handlePageChange}
        />
      )}
    </SearchPageLayout>
  );
};

export default ImageUploadSearchResultPage;
